import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { apiClient } from '@/lib/api-client';
import { Game } from '@/features/games/games-types';
import { useEnvironments } from '@/features/environments/environments-queries';
import {
  CreateGameBaseUrlInput,
  GameBaseUrl,
  UpdateGameBaseUrlInput,
} from './game-base-urls-types';
import { GameBaseUrlsForm } from './game-base-urls-form';
import {
  useCreateGameBaseUrl,
  useDeleteGameBaseUrl,
  useGameBaseUrls,
  useUpdateGameBaseUrl,
} from './game-base-urls-queries';

function useGame(gameId: string) {
  return useQuery({
    queryKey: ['games', gameId] as const,
    queryFn: async () => {
      const r = await apiClient.get<Game>(`/admin/games/${gameId}`);
      return r.data;
    },
    enabled: !!gameId,
  });
}

export function GameBaseUrlsPage(): JSX.Element {
  const { id: gameId } = useParams<{ id: string }>();
  if (!gameId) {
    return <p className="text-destructive">Missing game id.</p>;
  }

  const { data: game, isLoading: gameLoading, error: gameError } = useGame(gameId);
  const { data: envs } = useEnvironments({ is_active: true });
  const { data: rows, isLoading, error } = useGameBaseUrls(gameId);
  const createMut = useCreateGameBaseUrl(gameId);
  const updateMut = useUpdateGameBaseUrl(gameId);
  const deleteMut = useDeleteGameBaseUrl(gameId);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<GameBaseUrl | null>(null);
  const [deleting, setDeleting] = useState<GameBaseUrl | null>(null);

  // Build a code->name lookup so the table can render human-readable env names.
  const envById = useMemo(() => {
    const map = new Map<string, { code: string; name: string }>();
    for (const e of envs ?? []) map.set(e.id, { code: e.code, name: e.name });
    return map;
  }, [envs]);

  if (gameLoading) return <p className="text-muted-foreground">Loading…</p>;
  if (gameError) return <p className="text-destructive">Failed to load game.</p>;
  if (!game) return <p className="text-destructive">Game not found.</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/games" className="inline-flex items-center hover:text-foreground">
              <ArrowLeft className="mr-1 h-4 w-4" /> Games
            </Link>
            <span>/</span>
            <span className="font-mono">{game.package_name}</span>
          </div>
          <h2 className="mt-1 text-2xl font-semibold tracking-tight">Base URLs</h2>
          <p className="text-sm text-muted-foreground">
            Admin-only catalog of URLs grouped by environment. Pick from this list when
            creating a version mapping — replaces manual text input.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} disabled={!envs || envs.length === 0}>
          <Plus className="mr-2 h-4 w-4" /> New URL
        </Button>
      </div>

      {(!envs || envs.length === 0) && (
        <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
          No environments available. Add at least one in <Link to="/environments" className="underline">Environments</Link> first.
        </div>
      )}

      {isLoading && <p className="text-muted-foreground">Loading…</p>}
      {error && <p className="text-destructive">Failed to load catalog.</p>}

      {rows && rows.length === 0 && (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground">
            No catalog entries yet. Add a URL per environment to enable the
            version-mapping dropdown.
          </p>
        </div>
      )}

      {rows && rows.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Environment</TableHead>
              <TableHead>Base URL</TableHead>
              <TableHead className="w-32">State</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const env = envById.get(r.environment_id);
              return (
                <TableRow key={r.id}>
                  <TableCell>
                    <div className="font-mono text-sm">{env?.code ?? r.environment_id.slice(0, 8)}</div>
                    <div className="text-xs text-muted-foreground">{env?.name}</div>
                  </TableCell>
                  <TableCell className="font-mono text-sm break-all">{r.base_url}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 text-xs">
                      <span>{r.is_active ? '✓ Active' : '— Inactive'}</span>
                      {r.is_deprecated && (
                        <span className="rounded bg-yellow-100 px-2 py-0.5 text-yellow-900">
                          ⚠ Deprecated
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(r)} aria-label="Edit">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleting(r)} aria-label="Delete">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Base URL</DialogTitle>
          </DialogHeader>
          {envs && (
            <GameBaseUrlsForm
              environments={envs}
              onSubmit={async (data) => {
                await createMut.mutateAsync(data as CreateGameBaseUrlInput);
                setCreateOpen(false);
              }}
              onCancel={() => setCreateOpen(false)}
              isSubmitting={createMut.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Base URL</DialogTitle>
          </DialogHeader>
          {editing && envs && (
            <GameBaseUrlsForm
              initial={editing}
              environments={envs}
              onSubmit={async (data) => {
                await updateMut.mutateAsync({ id: editing.id, input: data as UpdateGameBaseUrlInput });
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
              isSubmitting={updateMut.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete catalog entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the URL from the dropdown for{' '}
              <code>
                {deleting && envById.get(deleting.environment_id)?.code}
              </code>
              . Existing version mappings that selected this URL are NOT affected —
              <code> version_mappings.base_url</code> keeps its copy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleting) {
                  await deleteMut.mutateAsync(deleting.id);
                  setDeleting(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
